import React, { useState, useEffect, useContext } from 'react';
import { CHANNEL_NAME, CREATE_ENVIRONMENT_PERMISSION_MESSAGE, DELETE_CHANNEL_PERMISSION_MESSAGE, DELETE_ENVIRONMENT_PERMISSION_MESSAGE, NO_OF_ENVIRONMENT, UPDATE_CHANNEL_PERMISSION_MESSAGE, UPDATE_ENVIRONMENT_PERMISSION_MESSAGE } from '../Utils/Constants';
import TableCell from 'Components/Common/components/TableCell';
import { DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import { DropdownMenu, DropdownMenuItem } from 'Pages/GMS/ISR/UI-Model/components/Common/DropdownMenu';
import { ConfirmationDialog } from 'Pages/GMS/ISR/UI-Model/components/Common/ConfirmationDialog';
import { toast } from 'react-toastify';

import { AddEnvironmentDialog } from '../Environment/AddEnvironmentDialog';
import { CreateChannelDialog } from '../Channel/CreateChannelDialog';
import EnvironmentSidePanel from '../Environment/EnvironmentSidePanel';

import { TChannel, TEnvironment, TEnvironmentPayload, TUpdateEnvironmentPayload } from '../Types/channel.types';
import { TUpdateDomainPayload } from '../Types/domain.types';
import EnvironmentList from '../Environment/EnvironmentList';

import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';
import { AppContext } from 'Context/AppContext';
import { EnvironmentAPIResponse } from '../Types/env.types';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

type ChannelTableProps = {
    partyId: string;
    domainId: string;
    searchTerm: string;
    showLoader: boolean;
    channels: TChannel[];
    setChannels: React.Dispatch<React.SetStateAction<TChannel[]>>;
    setShowLoader: (value: boolean) => void;
    setTotalRecords: (value: number) => void;
    paginationParams: { pageNumber: number; pageSize: number };
    onChannelDeleted?: () => void;
    refreshTrigger?: number;
    onChannelsLoaded?: () => void;
    readPermission: any
    createPermission: any
    updatePermission: any
    deletePermission: any
}

const ChannelTable = ({ partyId, domainId, searchTerm, showLoader, channels, setChannels, setShowLoader, setTotalRecords, paginationParams, onChannelDeleted, refreshTrigger, onChannelsLoaded, readPermission, updatePermission, deletePermission, createPermission }: ChannelTableProps) => {
    const appContext = useContext(AppContext);
    const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());
    const [channelEnvironments, setChannelEnvironments] = useState<Record<string, TEnvironment[]>>({});
    const [loadingEnvironments, setLoadingEnvironments] = useState<Set<string>>(new Set());
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<string>('asc');
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; channelIndex: number | null; channelName: string; channelId: string | null; }>({ isOpen: false, channelIndex: null, channelName: '', channelId: null });
    const [isDeleting, setIsDeleting] = useState(false);
    const [addEnvironmentDialog, setAddEnvironmentDialog] = useState<{ isOpen: boolean; channelId: string | null; channelName: string; }>({ isOpen: false, channelId: null, channelName: '' });
    const [isCreatingEnvironment, setIsCreatingEnvironment] = useState(false);
    const [isDeletingEnvironment, setIsDeletingEnvironment] = useState(false);
    const [deleteEnvironmentConfirmation, setDeleteEnvironmentConfirmation] = useState<{
        isOpen: boolean;
        environment: TEnvironment | null;
    }>({
        isOpen: false,
        environment: null
    });
    const [editChannelDialog, setEditChannelDialog] = useState<{
        isOpen: boolean;
        channel: TChannel | null;
    }>({
        isOpen: false,
        channel: null
    });
    const [isUpdatingChannel, setIsUpdatingChannel] = useState(false);
    const [editEnvironmentDialog, setEditEnvironmentDialog] = useState<{
        isOpen: boolean;
        environment: TEnvironment | null;
    }>({
        isOpen: false,
        environment: null
    });
    const [isUpdatingEnvironment, setIsUpdatingEnvironment] = useState(false);
    const [productionChangeConfirmation, setProductionChangeConfirmation] = useState<{
        isOpen: boolean;
        formData: any;
        isCreating: boolean;
    }>({
        isOpen: false,
        formData: null,
        isCreating: true
    });
    const [environmentSidePanel, setEnvironmentSidePanel] = useState<{
        isOpen: boolean;
        environment: TEnvironment | null;
        channelId: string | null;
        mode: 'create' | 'edit';
    }>({
        isOpen: false,
        environment: null,
        channelId: null,
        mode: 'create'
    });

    useEffect(() => {
        if (partyId && domainId) {
            fetchChannels();
        }
    }, [partyId, domainId, paginationParams, searchTerm, sortBy, sortOrder, refreshTrigger]);

    const fetchChannels = async () => {
        setShowLoader(true);
        try {
            const channelResponse = await ServiceWrapper.doGet(
                `${api_base_url}party/${partyId}/industry/${domainId}/parent_channel?page=${paginationParams.pageNumber}&per_page=${paginationParams.pageSize}&sort_by=${sortBy}&sort_order=${sortOrder}&search_string=${searchTerm}`,
                null,
                null,
                AuthManager.getGmsToken()
            );
            const response = channelResponse?.data;
            if (response && response.data) {
                setChannels(response.data);
                setTotalRecords(response.pagination ? response.pagination.total : response.data.length);
            }
        } catch (error) {
            console.error('Error fetching channels:', error);
            toast.error('Failed to fetch channels. Please try again.');
        } finally {
            setShowLoader(false);
            if (onChannelsLoaded) {
                onChannelsLoaded();
            }
        }
    };

    const fetchEnvironments = async (channelId: string) => {
        setLoadingEnvironments(prev => new Set(Array.from(prev).concat(channelId)));
        try {
            const envResponse = await ServiceWrapper.doGet(`${api_base_url}parent_channel/${channelId}/channel`, null, null, AuthManager.getGmsToken(),);
            const response = envResponse?.data;
            if (response && response.data) {
                const environments: TEnvironment[] = response.data.map((env: any) => ({
                    id: env.id,
                    name: env.name,
                    key: env.key,
                    isProduction: env.is_prod,
                    channelId: channelId,
                    created_at: env.created_at,
                    updated_at: env.updated_at
                }));

                setChannelEnvironments(prev => ({
                    ...prev,
                    [channelId]: environments
                }));
            }
        } catch (error) {
            console.error('Error fetching environments:', error);
            toast.error('Failed to fetch environments. Please try again.');
        } finally {
            setLoadingEnvironments(prev => {
                const newSet = new Set(Array.from(prev));
                newSet.delete(channelId);
                return newSet;
            });
        }
    };

    const toggleChannelExpansion = async (channelId: string, hasEnvironments: boolean) => {
        const newExpanded = new Set(expandedChannels);
        if (newExpanded.has(channelId)) {
            newExpanded.delete(channelId);
        } else {
            newExpanded.add(channelId);
            if (hasEnvironments && !channelEnvironments[channelId]) {
                await fetchEnvironments(channelId);
            }
        }
        setExpandedChannels(newExpanded);
    };

    const renderSortIcon = (column: string) => {
        if (sortBy !== column) {
            return <i className="fas fa-sort tw-text-gray-400 tw-ml-1" />;
        }
        return sortOrder === 'asc' ?
            <i className="fas fa-sort-up tw-text-gray-600 tw-ml-1" /> :
            <i className="fas fa-sort-down tw-text-gray-600 tw-ml-1" />;
    };

    const handleColumnClick = (column: string) => {
        if (!showLoader) {
            if (sortBy === column) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
            } else {
                setSortBy(column);
                setSortOrder('asc');
            }
        }
    };

    const handleEdit = (channel: TChannel, index: number) => {
        if (!updatePermission) {
            toast.warn(UPDATE_CHANNEL_PERMISSION_MESSAGE);
            return;
        }
        setEditChannelDialog({
            isOpen: true,
            channel: channel
        });
    };

    const handleUpdateChannel = async (formData: any) => {
        const channel = editChannelDialog.channel;
        if (!channel) return;
        setIsUpdatingChannel(true);
        try {
            const payload: TUpdateDomainPayload = {
                name: formData.channelName
            };
            const channelResponse = await ServiceWrapper.doPut(`${api_base_url}parent_channel/${channel.id}`, payload, null, AuthManager.getGmsToken(),);
            const response = channelResponse?.data;
            if (response && response.done) {
                toast.success(response?.message ?? 'Channel updated successfully!');
                setEditChannelDialog({ isOpen: false, channel: null });
                await fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update channel');
            }
        } catch (error) {
            console.error('Error updating channel:', error);
            toast.error('An error occurred while updating the channel');
        } finally {
            setIsUpdatingChannel(false);
        }
    };

    const handleDeleteClick = (channel: TChannel, index: number) => {
        if (!deletePermission) {
            toast.warn(DELETE_CHANNEL_PERMISSION_MESSAGE);
            return;
        }
        setDeleteConfirmation({
            isOpen: true,
            channelIndex: index,
            channelName: channel.name,
            channelId: channel.id
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation.channelId) return;
        setIsDeleting(true);
        try {
            const deleteResponse = await ServiceWrapper.doDelete(`${api_base_url}parent_channel/${deleteConfirmation.channelId}`, null, null, AuthManager.getGmsToken(),);
            const response = deleteResponse?.data;
            if (response && response.done) {
                toast.success(response.message || 'Channel deleted successfully!');
                await fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to delete channel');
                return;
            }
        } catch (error) {
            toast.error('Failed to delete channel. Please try again.');
        } finally {
            setIsDeleting(false);
            setDeleteConfirmation({
                isOpen: false,
                channelIndex: null,
                channelName: '',
                channelId: null
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation({
            isOpen: false,
            channelIndex: null,
            channelName: '',
            channelId: null
        });
    };

    const handleAddEnvironmentClick = (channel: TChannel) => {
        if (!createPermission) {
            toast.warn(CREATE_ENVIRONMENT_PERMISSION_MESSAGE);
            return;
        }
        if (channel.environment_count >= 3) {
            toast.error('Maximum 3 environments allowed per channel');
            return;
        }

        setEnvironmentSidePanel({
            isOpen: true,
            environment: null,
            channelId: channel.id,
            mode: 'create'
        });

        setAddEnvironmentDialog({
            isOpen: true,
            channelId: channel.id,
            channelName: channel.name
        });
    };

    const handleAddEnvironmentSave = async (formData: any) => {
        if (!addEnvironmentDialog.channelId || !partyId || !domainId) return;

        const channel = channels.find(ch => ch.id === addEnvironmentDialog.channelId);
        if (channel && channel.environment_count >= 3) {
            toast.error('Maximum 3 environments allowed per channel');
            return;
        }

        if (formData.useAsProduction && addEnvironmentDialog.channelId) {
            const environments = channelEnvironments[addEnvironmentDialog.channelId] || [];
            const hasProduction = environments.some(env => env.isProduction);

            if (hasProduction) {
                setProductionChangeConfirmation({
                    isOpen: true,
                    formData: formData,
                    isCreating: true
                });
                return;
            }
        }

        await createEnvironmentWithData(formData);
    };

    const createEnvironmentWithData = async (formData: any) => {
        setIsCreatingEnvironment(true);

        try {
            const payload: TEnvironmentPayload = {
                party_id: partyId,
                name: formData.environmentName,
                industry_id: domainId,
                is_prod: formData.useAsProduction || false,
                parent_channel_id: addEnvironmentDialog.channelId ?? ''
            };

            const envResponse = await ServiceWrapper.doPost(`${api_base_url}channel`, payload, null, AuthManager.getGmsToken(),);
            const response = envResponse?.data
            if (response && response.done) {
                toast.success('Environment added successfully!');
                setAddEnvironmentDialog({ isOpen: false, channelId: null, channelName: '' });

                if (expandedChannels.has(addEnvironmentDialog.channelId ?? '')) {
                    await fetchEnvironments(addEnvironmentDialog.channelId ?? '');
                }

                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to add environment');
            }
        } catch (error) {
            console.error('Error adding environment:', error);
            toast.error('An error occurred while adding the environment');
        } finally {
            setIsCreatingEnvironment(false);
        }
    };

    const handleEditEnvironment = (environment: TEnvironment) => {
        if (!updatePermission) {
            toast.warn(UPDATE_ENVIRONMENT_PERMISSION_MESSAGE);
            return;
        }
        setEnvironmentSidePanel({
            isOpen: true,
            environment: environment,
            channelId: environment.channelId,
            mode: 'edit'
        });
    };

    const handleUpdateEnvironment = async (formData: any) => {
        const environment = editEnvironmentDialog.environment;
        if (!environment) return;

        if (formData.useAsProduction && !environment.isProduction && environment.channelId) {
            const environments = channelEnvironments[environment.channelId] || [];
            const hasProduction = environments.some(env => env.isProduction && env.id !== environment.id);

            if (hasProduction) {
                setProductionChangeConfirmation({
                    isOpen: true,
                    formData: formData,
                    isCreating: false
                });
                return;
            }
        }

        await updateEnvironmentWithData(formData);
    };

    const updateEnvironmentWithData = async (formData: any) => {
        const environment = editEnvironmentDialog.environment;
        if (!environment) return;

        setIsUpdatingEnvironment(true);

        try {

            const payload: TUpdateEnvironmentPayload = {
                name: formData.environmentName,
                is_prod: formData.useAsProduction || false
            };

            const envResponse = await ServiceWrapper.doPost(`${api_base_url}channel/${environment.id}`, payload, null, AuthManager.getGmsToken(),);
            const response = envResponse?.data;

            if (response && response.done) {
                toast.success('Environment updated successfully!');
                setEditEnvironmentDialog({ isOpen: false, environment: null });

                if (environment.channelId && expandedChannels.has(environment.channelId)) {
                    await fetchEnvironments(environment.channelId);
                }

                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update environment');
            }
        } catch (error) {
            console.error('Error updating environment:', error);
            toast.error('An error occurred while updating the environment');
        } finally {
            setIsUpdatingEnvironment(false);
        }
    };

    const handleProductionChangeConfirm = async () => {
        const { formData, isCreating } = productionChangeConfirmation;

        setProductionChangeConfirmation({
            isOpen: false,
            formData: null,
            isCreating: true
        });

        if (isCreating) {
            await createEnvironmentWithData(formData);
        } else {
            await updateEnvironmentWithData(formData);
        }
    };

    const handleProductionChangeCancel = () => {
        setProductionChangeConfirmation({
            isOpen: false,
            formData: null,
            isCreating: true
        });
    };

    const handleEnvironmentSidePanelSave = async (formData: EnvironmentAPIResponse) => {
        console.log("🚀 ~ handleEnvironmentSidePanelSave ~ formData:", formData)
        const { mode, environment, channelId } = environmentSidePanel;

        if (mode === 'create') {
            if (!channelId || !partyId || !domainId) return;

            const channel = channels.find(ch => ch.id === channelId);
            if (channel && channel.environment_count >= 3) {
                toast.error('Maximum 3 environments allowed per channel');
                return;
            }

            // Check for production conflict
            if (formData.basic_info.is_prod && channelId) {
                const environments = channelEnvironments[channelId] || [];
                const hasProduction = environments.some(env => env.isProduction);

                if (hasProduction) {
                    setProductionChangeConfirmation({
                        isOpen: true,
                        formData: formData,
                        isCreating: true
                    });
                    return;
                }
            }

            await createEnvironmentWithSidePanelData(formData, channelId);
        } else {
            // Edit mode
            if (!environment) return;

            if (formData.basic_info.is_prod && !environment.isProduction && environment.channelId) {
                const environments = channelEnvironments[environment.channelId] || [];
                const hasProduction = environments.some(env => env.isProduction && env.id !== environment.id);

                if (hasProduction) {
                    setProductionChangeConfirmation({
                        isOpen: true,
                        formData: formData,
                        isCreating: false
                    });
                    return;
                }
            }

            await updateEnvironmentWithSidePanelData(formData, environment);
        }
    };

    const createEnvironmentWithSidePanelData = async (formData: any, channelId: string) => {
        setIsCreatingEnvironment(true);

        try {
            const payload: TEnvironmentPayload = {
                party_id: partyId,
                name: formData.name,
                industry_id: domainId,
                is_prod: formData.isProduction || false,
                parent_channel_id: channelId
            };

            const envResponse = await ServiceWrapper.doPost(
                `${api_base_url}channel`,
                payload,
                null,
                AuthManager.getGmsToken()
            );
            const response = envResponse?.data;

            if (response && response.done) {
                toast.success('Environment added successfully!');
                setEnvironmentSidePanel({
                    isOpen: false,
                    environment: null,
                    channelId: null,
                    mode: 'create'
                });

                if (expandedChannels.has(channelId)) {
                    await fetchEnvironments(channelId);
                }

                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to add environment');
            }
        } catch (error) {
            console.error('Error adding environment:', error);
            toast.error('An error occurred while adding the environment');
        } finally {
            setIsCreatingEnvironment(false);
        }
    };

    const updateEnvironmentWithSidePanelData = async (payload: EnvironmentAPIResponse, environment: TEnvironment) => {
        setIsUpdatingEnvironment(true);


        try {
            const envResponse = await ServiceWrapper.doPost(
                `${api_base_url}channel/${environment.id}`,
                payload,
                null,
                AuthManager.getGmsToken()
            );
            const response = envResponse?.data;

            if (response && response.done) {
                toast.success('Environment updated successfully!');
                setEnvironmentSidePanel({
                    isOpen: false,
                    environment: null,
                    channelId: null,
                    mode: 'create'
                });

                if (environment.channelId && expandedChannels.has(environment.channelId)) {
                    await fetchEnvironments(environment.channelId);
                }

                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update environment');
            }
        } catch (error) {
            console.error('Error updating environment:', error);
            toast.error('An error occurred while updating the environment');
        } finally {
            setIsUpdatingEnvironment(false);
        }
    };

    const handleDeleteEnvironmentClick = (environment: TEnvironment) => {
        if (!deletePermission) {
            toast.warn(DELETE_ENVIRONMENT_PERMISSION_MESSAGE);
            return;
        }
        setDeleteEnvironmentConfirmation({
            isOpen: true,
            environment: environment
        });
    };

    const handleDeleteEnvironmentConfirm = async () => {
        const environment = deleteEnvironmentConfirmation.environment;
        if (!environment) return;
        setIsDeletingEnvironment(true);
        try {
            const envResponse = await ServiceWrapper.doDelete(`${api_base_url}channel/${environment.id}`, null, null, AuthManager.getGmsToken(),);
            const response = envResponse?.data;
            if (response && response.done) {
                toast.success('Environment deleted successfully!');
                if (environment.channelId &&
                    expandedChannels.has(environment.channelId)) {
                    await fetchEnvironments(environment.channelId);
                }
                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to delete environment');
            }
        } catch (error) {
            console.error('Error deleting environment:', error);
            toast.error('Failed to delete environment. Please try again.');
        } finally {
            setIsDeletingEnvironment(false);
            setDeleteEnvironmentConfirmation({
                isOpen: false,
                environment: null
            });
        }
    };

    const handleDeleteEnvironmentCancel = () => {
        setDeleteEnvironmentConfirmation({
            isOpen: false,
            environment: null
        });
    };

    const getDropdownMenuItems = (channel: TChannel, index: number): DropdownMenuItem[] => [
        {
            id: `edit-${index}`,
            label: 'Edit',
            className: 'tw-text-[#172B4D]',
            onClick: () => handleEdit(channel, index),
            icon: (
                <EditIcon />
            )
        },
        {
            id: `delete-${index}`,
            label: 'Delete',
            onClick: () => handleDeleteClick(channel, index),
            className: 'tw-text-[#172B4D]',
            divider: true,
            icon: (
                <DeleteIcon />
            )
        }
    ];

    const renderExpandIcon = (channelId: string) => {
        const isExpanded = expandedChannels.has(channelId);
        return (
            <svg
                className={`tw-w-4 tw-h-4 tw-transition-transform ${isExpanded ? 'tw-rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
        );
    };

    const renderTableContent = () => {
        if (showLoader) {
            return (
                <tr>
                    <td colSpan={3} className="tw-text-center tw-py-8">
                        <div className="tw-inline-flex tw-items-center">
                            <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-mr-3 tw-text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading channels...
                        </div>
                    </td>
                </tr>
            );
        }

        if (channels.length === 0) {
            return (
                <tr>
                    <td colSpan={3} className="tw-text-center tw-py-8 tw-text-gray-500">
                        No channels found
                    </td>
                </tr>
            );
        }

        return channels.map((channel, index) => {
            const isExpanded = expandedChannels.has(channel.id);
            const hasEnvironments = channel.environment_count > 0;
            const environments = channelEnvironments[channel.id] || [];
            const isLoadingEnv = loadingEnvironments.has(channel.id);

            return (
                <React.Fragment key={channel.id}>
                    <tr style={{ position: 'relative' }}>
                        <TableCell id={`channel-name-${index}`} rowIndex={index} style={{ width: '60%' }}>
                            <span>{channel.name}</span>
                        </TableCell>
                        <TableCell id={`no-of-environments-${index}`} rowIndex={index} style={{ width: '20%' }}>
                            {channel?.environment_count !== 0 ? channel?.environment_count :
                                <div className="tw-text-start">
                                    <button
                                        onClick={() => handleAddEnvironmentClick(channel)}
                                        className="button-style"
                                    >
                                        Add Environment
                                    </button>
                                </div>
                            }
                            {channel?.environment_count >= 3 && (
                                <span className="tw-text-xs tw-text-gray-500 tw-ml-2">(Max limit reached)</span>
                            )}
                        </TableCell>
                        <TableCell id={`actions-${index}`} rowIndex={index} style={{ position: 'relative', overflow: 'visible', }}>
                            <div className="tw-flex tw-items-center tw-gap-5">
                                <DropdownMenu
                                    items={getDropdownMenuItems(channel, index)}
                                    trigger={
                                        <button id={`overflow-btn-${index}`} className="tw-p-1 hover:tw-bg-gray-100 tw-rounded">
                                            <OverFlowIcon />
                                        </button>
                                    }
                                    position="right"
                                />

                                {hasEnvironments && (
                                    <button
                                        onClick={() => toggleChannelExpansion(channel.id, hasEnvironments)}
                                        className="tw-p-1 hover:tw-bg-gray-100 tw-rounded"
                                        disabled={isLoadingEnv}
                                    >
                                        {isLoadingEnv ? (
                                            <svg className="tw-animate-spin tw-h-4 tw-w-4 tw-text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : (
                                            renderExpandIcon(channel.id)
                                        )}
                                    </button>
                                )}

                            </div>
                        </TableCell>
                    </tr>

                    {isExpanded && hasEnvironments && (
                        <tr>
                            <td colSpan={3} className="tw-p-0">
                                <div className="tw-bg-gray-50 tw-border tw-border-gray-200">
                                    <EnvironmentList
                                        environments={environments}
                                        isLoading={isLoadingEnv}
                                        onAddEnvironment={() => handleAddEnvironmentClick(channel)}
                                        onEditEnvironment={handleEditEnvironment}
                                        onDeleteEnvironment={handleDeleteEnvironmentClick}
                                    />
                                </div>
                            </td>
                        </tr>
                    )}
                </React.Fragment>
            );
        });
    };

    return (
        <>
            <div id="channel-table" className="tw-border tw-border-solid tw-mt-3 tw-border-[#DFE0E6] tw-border-r-2" style={{ overflow: 'visible' }}>
                <div id="table-scroll-container" className="table-container" style={{ width: '100%', height: 'auto', overflowX: 'auto', overflowY: 'visible' }}>
                    <table id="channel-data-table" className="tw-bg-[#DFE1E6] tw-w-full tw-table-fixed">
                        <thead id="channel-table-header" className="tw-sticky tw-top-0 tw-bg-[#DFE1E6]">
                            <tr>
                                <th
                                    id="channel-name-header"
                                    className="table-header-style tw-cursor-pointer hover:tw-bg-gray-200"
                                    style={{ width: '60%' }}
                                    onClick={() => handleColumnClick('name')}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{CHANNEL_NAME}</span>
                                        {renderSortIcon('name')}
                                    </div>
                                </th>
                                <th
                                    id="no-of-environment-header"
                                    className="table-header-style tw-cursor-pointer hover:tw-bg-gray-200"
                                    style={{ width: '30%' }}
                                    onClick={() => handleColumnClick('environment_count')}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{NO_OF_ENVIRONMENT}</span>
                                        {renderSortIcon('environment_count')}
                                    </div>
                                </th>
                                <th id="actions-header" className="table-header-style tw-w-[10%]"></th>
                            </tr>
                        </thead>
                        <tbody id="channel-table-body" className="tw-bg-white tw-divide-y tw-divide-[#F4F5F7]" style={{ position: 'relative' }}>
                            {renderTableContent()}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmationDialog
                isOpen={deleteConfirmation.isOpen}
                title="Delete Channel"
                message={`Are you sure you want to delete the channel "${deleteConfirmation.channelName}"?`}
                secondaryMessage={'This action cannot be undone.'}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                isLoading={isDeleting}
            />

            <AddEnvironmentDialog
                isOpen={addEnvironmentDialog.isOpen}
                onClose={() => setAddEnvironmentDialog({ isOpen: false, channelId: null, channelName: '' })}
                onSave={handleAddEnvironmentSave}
                isLoading={isCreatingEnvironment}
                hasProductionEnv={addEnvironmentDialog.channelId ? (channelEnvironments[addEnvironmentDialog.channelId] || []).some(env => env.isProduction) : false}
            />

            <ConfirmationDialog
                isOpen={deleteEnvironmentConfirmation.isOpen}
                title="Delete Environment"
                message={`Are you sure you want to delete the environment "${deleteEnvironmentConfirmation.environment?.name}"?`}
                secondaryMessage={'This action cannot be undone.'}
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDeleteEnvironmentConfirm}
                onCancel={handleDeleteEnvironmentCancel}
                isLoading={isDeletingEnvironment}
            />

            <CreateChannelDialog
                isOpen={editChannelDialog.isOpen}
                onClose={() => setEditChannelDialog({ isOpen: false, channel: null })}
                onSave={handleUpdateChannel}
                isLoading={isUpdatingChannel}
                editData={editChannelDialog.channel}
                mode="edit"
            />

            <AddEnvironmentDialog
                isOpen={editEnvironmentDialog.isOpen}
                onClose={() => setEditEnvironmentDialog({ isOpen: false, environment: null })}
                onSave={handleUpdateEnvironment}
                isLoading={isUpdatingEnvironment}
                editData={editEnvironmentDialog.environment}
                mode="edit"
                hasProductionEnv={editEnvironmentDialog.environment?.channelId ? (channelEnvironments[editEnvironmentDialog.environment.channelId] || []).some(env => env.isProduction) : false}
            />

            <ConfirmationDialog
                isOpen={productionChangeConfirmation.isOpen}
                title="Change Production Environment"
                message="You are changing the production environment. Are you sure?"
                confirmText="Yes, Change"
                cancelText="Cancel"
                confirmButtonClass="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white"
                onConfirm={handleProductionChangeConfirm}
                onCancel={handleProductionChangeCancel}
            />

            {environmentSidePanel.environment?.id && <EnvironmentSidePanel
                isOpen={environmentSidePanel.isOpen || false}
                onClose={() =>
                    setEnvironmentSidePanel({
                        isOpen: false,
                        environment: null,
                        channelId: null,
                        mode: 'create'
                    })
                }
                editData={environmentSidePanel.environment}
                onSave={handleEnvironmentSidePanelSave}
                isLoading={isCreatingEnvironment || isUpdatingEnvironment}
                selectedEnvironmentId={environmentSidePanel.environment?.id}
                selectedEnvironmentName={environmentSidePanel.environment?.name}
                allEnvironmentList={environmentSidePanel.environment?.channelId ? (channelEnvironments[environmentSidePanel.environment.channelId] || []).map(env => env.name) : []}
                hasProductionEnv={
                    environmentSidePanel.environment?.channelId
                        ? (channelEnvironments[environmentSidePanel.environment.channelId] || [])
                            .some(env => env.isProduction && env.id !== environmentSidePanel.environment?.id)
                        : false
                }
            />}
        </>
    );
}

export default ChannelTable;